import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api.js';
import Alert from '../../components/ui/Alert.jsx';
import AiMark from '../../components/ui/AiMark.jsx';
import ConfirmModal from '../../components/ui/ConfirmModal.jsx';
import { fmtDateTime } from '../../utils/format.js';
import { defang } from './inboxShared.jsx';

// Track down a confirmed scam: where it came from, the companies behind the domains it
// uses, and reviewed reports to their abuse desks (API: routes/investigations.js).

const KIND = { registrar: 'Domain registrar', host: 'Web host', network: 'Network', email_service: 'Email service', email_provider: 'Email provider' };
const ROLE = { sender: 'sent from', reply_to: 'replies go to', link: 'linked to', email: 'asks people to write to' };
const STATUS = { drafting: ['Writing the draft…', 'neutral'], draft: ['Draft: check it and send', 'warn'], sent: ['Sent', 'good'], failed: ['Couldn’t be drafted', 'bad'] };

const ageText = (d) => (d == null ? null : d < 1 ? 'registered today' : d < 60 ? `registered ${d} day${d === 1 ? '' : 's'} ago` : d < 730 ? `registered ${Math.round(d / 30)} months ago` : `registered ${Math.round(d / 365)} years ago`);

function ReportEditor({ report, base, onChange }) {
  const [subject, setSubject] = useState(report.subject || '');
  const [body, setBody] = useState(report.body || '');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(false);
  const url = `${base}/reports/${report.id}`;
  const save = async () => {
    setBusy('save'); setError('');
    try { await api(url, { method: 'PUT', body: { subject, body } }); onChange(); } catch (err) { setError(err.message); }
    setBusy('');
  };
  const send = async () => {
    setError('');
    await api(`${url}/send`, { method: 'POST', body: { subject, body } });
    setConfirm(false); onChange();
  };
  const discard = async () => { try { await api(url, { method: 'DELETE' }); onChange(); } catch (err) { setError(err.message); } };
  return (
    <div className="inv-editor">
      {error && <Alert tone="danger">{error}</Alert>}
      <p className="small muted">{report.ai_drafted ? <><AiMark /> Drafted by AI from the facts we found. </> : 'Written from the facts we found. '}Read it carefully and change anything that isn’t right before you send it.</p>
      <div className="field">
        <label htmlFor={`subj-${report.id}`}>Subject</label>
        <input id={`subj-${report.id}`} value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={300} />
      </div>
      <div className="field">
        <label htmlFor={`body-${report.id}`}>Report</label>
        <textarea id={`body-${report.id}`} rows={14} value={body} onChange={(e) => setBody(e.target.value)} maxLength={10000} />
        <p className="field-hint">The scam email’s full headers are added underneath when we have them.</p>
      </div>
      <div className="actions">
        <button type="button" className="btn btn-primary" onClick={() => setConfirm(true)} disabled={!!busy}>Send to {report.to_email}</button>
        <button type="button" className="btn btn-secondary" onClick={save} disabled={!!busy}>{busy === 'save' ? 'Saving…' : 'Save draft'}</button>
        <button type="button" className="btn btn-danger-ghost" onClick={discard} disabled={!!busy}>Discard</button>
      </div>
      {confirm && (
        <ConfirmModal title={`Send this report to ${report.provider_name}?`} confirmLabel="Send report" onClose={() => setConfirm(false)} onConfirm={send}>
          <p>It goes to <strong>{report.to_email}</strong> from “SummitShield Fraud Reporting” &lt;protect@summitshield.io&gt;, on behalf of your company. Their reply will appear here.</p>
        </ConfirmModal>
      )}
    </div>
  );
}

function ProviderCard({ p, reports, base, canReport, onChange }) {
  const [error, setError] = useState('');
  const mine = reports.filter((r) => r.provider_key === p.key);
  const open = mine.find((r) => ['drafting', 'draft', 'failed'].includes(r.status));
  const sent = mine.filter((r) => r.status === 'sent');
  const draft = async () => {
    setError('');
    try { await api(`${base}/reports`, { method: 'POST', body: { provider_key: p.key } }); onChange(); } catch (err) { setError(err.message); }
  };
  return (
    <li className="inv-provider">
      <div className="inv-provider-head">
        <div>
          <p className="inv-kind">{KIND[p.kind] || p.kind}{p.strength === 'proven' ? ' · proven' : ''}</p>
          <h4>{p.name}</h4>
          <p className="mono small">{defang(p.target)}</p>
        </div>
        {canReport && !open && (p.contact?.email
          ? <button type="button" className="btn btn-secondary btn-small" onClick={draft}>{sent.length ? 'Report again' : 'Draft a report'}</button>
          : p.contact?.form ? <a className="btn btn-secondary btn-small" href={p.contact.form} target="_blank" rel="noreferrer">Open their report form</a> : null)}
      </div>
      <ul className="inv-why">{p.why.map((w) => <li key={w}>Because {w}.</li>)}</ul>
      {!p.contact && <p className="muted small">We couldn’t find an abuse contact for them.</p>}
      {p.contact?.email && !open && !sent.length && <p className="muted small">Reports go to {p.contact.email}.</p>}
      {error && <Alert tone="danger">{error}</Alert>}
      {open && (
        <div className="inv-report">
          <span className={`msg-pill tone-${STATUS[open.status][1]}`}>{STATUS[open.status][0]}</span>
          {open.status === 'failed' && <p className="muted small">{open.error}</p>}
          {open.status === 'draft' && <ReportEditor report={open} base={base} onChange={onChange} />}
        </div>
      )}
      {sent.map((r) => (
        <div key={r.id} className="inv-report">
          <span className="msg-pill tone-good">Sent {fmtDateTime(r.sent_at)} to {r.to_email}</span>
          <details><summary className="small">What we sent</summary><p className="small"><strong>{r.subject}</strong></p><pre className="inv-sent">{r.body}</pre></details>
          {r.replies.length ? r.replies.map((x) => (
            <div key={x.id} className="inv-reply">
              <p className="small"><strong>Reply from {x.from || 'them'}</strong> · {fmtDateTime(x.received_at)}</p>
              <p className="small inv-reply-text">{x.text || x.subject}</p>
            </div>
          )) : <p className="muted small">No reply yet. Most abuse desks act without replying; we’ll show any answer here.</p>}
        </div>
      ))}
    </li>
  );
}

export default function Investigation({ organisationId, messageId }) {
  const base = `/portal/organisations/${organisationId}/inbox/${messageId}/investigation`;
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(() => api(base).then(setData).catch((err) => setError(err.message)), [base]);
  useEffect(() => { load(); }, [load]);
  // Still working (the lookups, or a draft being written): look again shortly
  const busy = data?.investigation?.status === 'running' || data?.reports?.some((r) => r.status === 'drafting');
  useEffect(() => {
    if (!busy) return undefined;
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [busy, data, load]);

  if (error) return <Alert tone="danger">{error}</Alert>;
  if (!data || !data.eligible) return null;
  const inv = data.investigation;
  const start = async () => { try { await api(base, { method: 'POST' }); load(); } catch (err) { setError(err.message); } };
  const o = inv?.origin;

  return (
    <section className="card ld-hero inv" aria-labelledby="inv-h">
      <div className="inv-head">
        <div>
          <h2 id="inv-h">Track it down</h2>
          <p className="muted">Find the companies the scammer relies on (the domain registrar, the web host, the email provider) and report it to them. They can suspend the domain or close the account, which stops the next email. <Link to="/help/tracking-scams">How this works</Link></p>
        </div>
        {data.can_report && inv?.status !== 'running' && (
          <button type="button" className={`btn ${inv ? 'btn-secondary btn-small' : 'btn-primary'}`} onClick={start}>{inv ? 'Look again' : 'Find who’s behind it'}</button>
        )}
      </div>
      {!inv && !data.can_report && <p className="muted">An owner or admin of your company can do this.</p>}
      {inv?.status === 'running' && <p className="muted">Looking it up: the sending server, the domains, who registered and hosts them… This takes up to a minute.</p>}
      {inv?.status === 'failed' && <Alert tone="danger">The lookup didn’t finish ({inv.error}). Try “Look again”.</Alert>}

      {inv?.status === 'done' && (
        <>
          <div className="inv-grid">
            <div className="inv-box">
              <h3>Where it came from</h3>
              {o?.proven && o.ip ? (
                <ul className="inv-facts">
                  <li>Sent from <span className="mono">{o.ip}</span>{o.ptr ? <> (<span className="mono">{o.ptr}</span>)</> : ''}</li>
                  {o.network && <li>Network: {o.network.name || 'unknown'}{o.network.asn ? ` (${o.network.asn})` : ''}</li>}
                  {o.network?.country && <li>Country of the network: {o.network.country}</li>}
                  {o.service && <li>Email service: {o.service}</li>}
                  {inv.sender && <li>From: <span className="mono">{defang(inv.sender)}</span></li>}
                </ul>
              ) : (
                <p className="muted small">It was forwarded as text, so we can’t see where it was really sent from. We’ve used what’s in the email itself. Ask people to use “Forward as attachment” next time.</p>
              )}
            </div>
            <div className="inv-box">
              <h3>The domains it uses</h3>
              {inv.domains?.length ? (
                <ul className="inv-facts">
                  {inv.domains.map((d) => (
                    <li key={d.domain}>
                      <span className="mono">{defang(d.domain)}</span> <span className="muted small">({d.roles.map((r) => ROLE[r] || r).join(', ')})</span>
                      <div className="small muted">
                        {[ageText(d.age_days), d.registrar?.name && `registrar ${d.registrar.name}`, d.host?.network?.name && `hosted by ${d.host.network.name}${d.host.network.country ? `, ${d.host.network.country}` : ''}`, d.lookalike_of && `made to look like ${d.lookalike_of}`].filter(Boolean).join(' · ') || 'No registration details found'}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : <p className="muted small">No domains of its own: it only uses free email accounts or your own addresses.</p>}
            </div>
          </div>

          <h3 className="inv-sub">Who to report it to</h3>
          {inv.providers?.length ? (
            <ul className="inv-providers">
              {inv.providers.map((p) => <ProviderCard key={p.key} p={p} reports={data.reports} base={base} canReport={data.can_report} onChange={load} />)}
            </ul>
          ) : <p className="muted">We couldn’t find anyone safe to report it to. We never report a domain only because it’s in the From line, because scammers fake that line using other people’s domains (sometimes yours).</p>}
          <p className="muted small">Reports are sent from “SummitShield Fraud Reporting” &lt;protect@summitshield.io&gt; on behalf of your company, only after you’ve read them. Looked up {fmtDateTime(inv.checked_at || inv.updated_at)}.</p>
        </>
      )}
    </section>
  );
}
